import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { sagaMiddleware } from 'store/configureStore';
import LoadingComponent from 'component/LoadingComponent/LoadingComponent';

import { database } from '../../firebase';
import { FEATURE_FLAGS_COLLECTION } from '../../constants';
import { collection, getDocs } from '@firebase/firestore';
import { IFlags } from 'shared/hooks/useFlags';

export type FlagsContextType = {
	flag: (k: string) => boolean;
	flags: IFlags;
	setFlags: React.Dispatch<React.SetStateAction<IFlags>>;
};

export const FlagsContext = createContext<FlagsContextType>({} as FlagsContextType);

type Props = {
	children: ReactNode;
};

/**
 * Provides feature flags to the React component tree via context.
 *
 * This component ensures that all child components can safely access feature flags
 * without needing to manage the logic for fetching or handling them.
 *
 * It delays rendering of children until flags are fully loaded to avoid inconsistent
 * UI behavior caused by undefined flags. This approach ensures a stable and predictable
 * feature-flag-driven rendering flow across the app.
 *
 * @param {Props} props - The children components to be rendered after flags are ready.
 */
export const FlagsProvider = ({ children }: Props) => {
	const [flags, setFlags] = useState({} as IFlags);
	const [isFlagsReady, setIsFlagsReady] = useState(false);

	const flag = useCallback((k: string) => flags[k], [flags]);

	useEffect(() => {
		sagaMiddleware.setContext({
			flag,
			flags,
		});
	}, [flag, flags]);

	const fetchFirebaseFlags = async () => {
		try {
			if (!database) {
				console.error('Firebase database is not initialized.');
				return;
			}

			const ref = await getDocs(collection(database, FEATURE_FLAGS_COLLECTION));

			/**
			 * Index 0: feature flag config for production
			 * Index 1: feature flag config for testing/staging
			 * Index 2: feature flag config for development
			 */
			let flagData = null;
			if (process.env.REACT_APP_NODE_ENV === 'production' && ref.docs[0]) {
				flagData = ref.docs[0].data();
			} else if (['testing', 'staging'].includes(process.env.REACT_APP_NODE_ENV || '') && ref.docs[1]) {
				/**
				 * @todo This is temporary and will be removed after all deadlines and tasks are completed.
				 * So there's no need to create a ticket, PM will do in the future
				 */
				flagData = ref.docs[1].data();
			} else if (process.env.REACT_APP_NODE_ENV === 'development' && ref.docs[2]) {
				flagData = ref.docs[2].data();
			}

			if (flagData) {
				setFlags(flagData);
			} else {
				console.error(`No flags found for environment: ${process.env.REACT_APP_NODE_ENV}`);
			}
		} catch (error) {
			console.error('Error accessing Firestore:', error);
		} finally {
			setIsFlagsReady(true);
		}
	};

	useEffect(() => {
		fetchFirebaseFlags();
	}, []);

	if (!isFlagsReady) {
		return <LoadingComponent />;
	}

	const values = {
		flag,
		flags,
		setFlags,
	};

	return <FlagsContext.Provider value={values}>{children}</FlagsContext.Provider>;
};
